import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { IPropertyEquipmentDto } from '@app/interfaces/property-equipment.interface';
import { IPropertyDto } from '@app/interfaces/property.interface';
import { IScreenDto } from '@app/interfaces/screen.interface';
import { createScreensPreview } from '@app/utils/screens-preview';
import { environment } from '@env/environment';
import { delay, Observable, of, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ScreensPreviewService {
    private readonly location = inject(DOCUMENT).location;
    private readonly equipments: IPropertyEquipmentDto[] = createScreensPreview().map(screen => {
        const dto = screen.dataTransferObject();
        return {
            ken: screen.ken,
            shadowKen: screen.shadowKen,
            activeContract: screen.activeContract,
            contractDate: '2026-01-01',
            contractEndDate: '2027-12-31',
            screens: [
                {
                    ...dto,
                    device_id: screen.deviceId,
                    serial_number: screen.serial_number,
                    functional_location: screen.functionalLocation,
                    linked: screen.linked,
                    active: true,
                    error: screen.error,
                    displayType: screen.displayType,
                    android_brand: 'Elo',
                    eleveight: {
                        ...dto.eleveight!,
                        model: 'I-Series 32',
                        custom_attributes: {
                            ...dto.eleveight!.custom_attributes,
                            connectivity_method: 'Ethernet',
                            operating_system: 'Android 13',
                        },
                        advertising_attributes: {
                            ...dto.eleveight!.advertising_attributes,
                            orientation: 'portrait',
                        },
                    },
                },
            ],
        };
    });
    private readonly properties: IPropertyDto[] = Array.from({ length: 4 }, (_, index) => ({
        active: true,
        propertyType: 'OFF',
        eleveight: {
            building_id: `DEMO-SITE-${index + 1}`,
            name: ['Harbour Offices', 'Central Plaza', 'Riverside House', 'Garden Tower'][index],
            street_name: 'Example Street',
            street_number: String(index + 1),
            postal_code: '00100',
            city: 'Helsinki',
            country: 'FI',
            latitude: 60.1699,
            longitude: 24.9384,
        },
        equipment: this.equipments.slice(index * 3, index * 3 + 3),
    }));

    readonly enabled =
        isDevMode() &&
        (new URLSearchParams(this.location.search).get('preview') === 'true' ||
            this.location.pathname.includes('/DEMO-'));

    respond(request: HttpRequest<unknown>): Observable<HttpResponse<unknown>> | undefined {
        if (!this.enabled) return undefined;
        const api = new URL(environment.api, this.location.href);
        const url = new URL(request.url, this.location.href);
        if (url.origin !== api.origin || !url.pathname.startsWith(`${api.pathname}/`))
            return undefined;
        const path = url.pathname.slice(api.pathname.length);
        const [, resource, id, operation] = path.split('/');
        const method = request.method;

        if (resource === 'admin' && id === 'sync') {
            this.equipments.forEach(equipment => this.refresh(equipment));
            return this.success({ success: true });
        }
        if (resource === 'equipment') {
            if (method === 'GET') return this.success(this.equipments);
            const equipment = this.equipments.find(item => item.ken === id);
            if (!equipment) return this.notFound();
            if (method === 'PUT' && operation === 'resync') {
                this.refresh(equipment);
                return this.success({ success: true });
            }
            const ken = request.params.get('newKen');
            if (method === 'PUT' && ken) {
                equipment.ken = ken;
                equipment.shadowKen = false;
                equipment.screens?.forEach(screen => {
                    screen.ken = ken;
                });
                return this.success({ success: true });
            }
            return this.notFound();
        }
        if (resource === 'screens') {
            const equipment = this.equipments.find(item =>
                item.screens?.some(screen => screen.device_id === id),
            );
            const screen = equipment?.screens?.find(item => item.device_id === id);
            if (!equipment || !screen) return this.notFound();
            if (method === 'GET') return this.success(screen);
            if (method === 'DELETE' && !operation) {
                equipment.screens = equipment.screens?.filter(item => item !== screen);
            } else if (operation === 'eleveight') {
                screen.active = method === 'POST';
            } else if (method === 'PUT') {
                Object.assign(screen, request.body as Partial<IScreenDto>);
            } else if (operation !== 'refreshurl') {
                return this.notFound();
            }
            return this.success(screen);
        }
        if (resource === 'properties') {
            if (method === 'GET' && !id) return this.success(this.properties);
            const property = this.properties.find(item => item.eleveight.building_id === id);
            if (!property) return this.notFound();
            if (method === 'GET') return this.success(property);
            if (method === 'PUT') Object.assign(property, request.body as Partial<IPropertyDto>);
            else if (operation === 'eleveight') property.active = method === 'POST';
            else if (method === 'DELETE')
                this.properties.splice(this.properties.indexOf(property), 1);
            else return this.notFound();
            return this.success(property);
        }
        // Never send mutations from a preview session to the live API.
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) return this.notFound();
        return undefined;
    }

    private refresh(equipment: IPropertyEquipmentDto) {
        equipment.screens?.forEach(screen => {
            screen.error = undefined;
        });
    }

    private success(body: unknown) {
        return of(new HttpResponse({ status: 200, body: structuredClone(body) })).pipe(delay(300));
    }

    private notFound(): Observable<never> {
        return throwError(
            () => new HttpErrorResponse({ status: 404, statusText: 'Preview route not found' }),
        );
    }
}
